import { type AdminSystem, LICENCE_STATUSES, type LicenceStatus } from '@fruuit/shared'
import { Badge, cn, Skeleton } from '@fruuit/ui'
import { CircleCheck, CircleDashed, Radio } from 'lucide-react'
import type { ReactNode } from 'react'
import { errorMessage } from '../../api/client'
import { useAdminSystem } from './api'
import type { LiveView } from './types'

/** Cron expressions the Worker registers, in words (UTC). */
const CRON_WORDS: Record<string, string> = {
  '0 * * * *': 'Hourly, on the hour (UTC)',
  '30 2 * * *': 'Nightly at 02:30 UTC',
}

const PROVIDER_LABEL = { google: 'Google', microsoft: 'Microsoft', email: 'Email code' } as const

function Frame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <figure className="docs-live overflow-hidden rounded-md border border-line bg-surface break-inside-avoid">
      <figcaption className="flex items-center gap-2 border-b border-line-soft bg-wash/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-teal-600">
        <Radio aria-hidden="true" className="size-3.5" />
        <span>Live · {title}</span>
      </figcaption>
      <div className="px-4 py-3">{children}</div>
    </figure>
  )
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-2 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] sm:gap-4">
      <dt className="text-sm font-medium text-sage">{label}</dt>
      <dd className="min-w-0 text-sm text-ink break-words">{children}</dd>
    </div>
  )
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-xs bg-wash px-1 py-px font-mono text-[0.86em] text-ink-soft">
      {children}
    </code>
  )
}

function OnOff({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <Badge tone={on ? 'positive' : 'quiet'} icon={on ? <CircleCheck /> : <CircleDashed />}>
      {on ? onLabel : offLabel}
    </Badge>
  )
}

function emails(list: string[]): ReactNode {
  if (list.length === 0) return <span className="text-sage">None</span>
  return (
    <span className="flex flex-wrap gap-1.5">
      {list.map((email) => (
        <Code key={email}>{email}</Code>
      ))}
    </span>
  )
}

function AuthPanel({ data }: { data: AdminSystem }) {
  const { auth } = data
  return (
    <dl className="divide-y divide-line-soft">
      <Row label={<Code>PRIVILEGED_MFA</Code>}>
        <Badge tone={auth.privileged_mfa === 'required' ? 'positive' : 'watch'}>
          {auth.privileged_mfa}
        </Badge>
      </Row>
      <Row label="Sign-in methods">
        <span className="flex flex-wrap gap-1.5">
          {(Object.keys(PROVIDER_LABEL) as (keyof typeof PROVIDER_LABEL)[]).map((key) => (
            <OnOff
              key={key}
              on={auth.providers[key]}
              onLabel={`${PROVIDER_LABEL[key]} on`}
              offLabel={`${PROVIDER_LABEL[key]} off`}
            />
          ))}
        </span>
      </Row>
      <Row label={<Code>ADMIN_DOMAIN</Code>}>
        <Code>@{auth.admin_domain}</Code>
      </Row>
      <Row label={<Code>ADMIN_EXTRA_EMAILS</Code>}>{emails(auth.admin_extra_emails)}</Row>
      <Row label={<Code>OPERATOR_EMAILS</Code>}>{emails(auth.operator_emails)}</Row>
      <Row label={<Code>FRUUIT_ENTRA_TID</Code>}>
        <OnOff on={auth.entra_tenant_configured} onLabel="Configured" offLabel="Missing" />
      </Row>
      {auth.email_dev_log ? (
        <Row label={<Code>EMAIL_DEV_LOG</Code>}>
          <Badge tone="watch">On: codes are logged, not emailed (local only)</Badge>
        </Row>
      ) : null}
    </dl>
  )
}

function FeedsPanel({ data }: { data: AdminSystem }) {
  return (
    <ul className="divide-y divide-line-soft">
      {data.feeds.map((feed) => (
        <li key={feed.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-ink">{feed.name}</span>
            <span className="text-xs text-sage">
              {feed.schedule} · late after {feed.late_after_minutes} min ·{' '}
              <Code>{feed.dataset}</Code>
            </span>
          </span>
          <span className="flex flex-wrap gap-1.5">
            {feed.licence_status !== 'simulated' ? (
              <Badge tone={feed.licence_status === 'licensed' ? 'positive' : 'attention'}>
                {LICENCE_STATUSES.labels[feed.licence_status as LicenceStatus] ??
                  feed.licence_status}
              </Badge>
            ) : null}
            {feed.enabled === null ? (
              <Badge tone="quiet">Not registered yet</Badge>
            ) : (
              <OnOff on={feed.enabled} onLabel="Enabled" offLabel="Disabled" />
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}

function CronPanel({ data }: { data: AdminSystem }) {
  return (
    <dl className="divide-y divide-line-soft">
      {data.cron.map((entry) => (
        <Row
          key={entry.cron}
          label={
            <span className="flex flex-col gap-0.5">
              <Code>{entry.cron}</Code>
              <span className="text-xs">{CRON_WORDS[entry.cron] ?? 'UTC'}</span>
            </span>
          }
        >
          <span className="flex flex-wrap gap-1.5">
            {entry.jobs.map((job) => (
              <Badge key={job} tone="info">
                {job}
              </Badge>
            ))}
          </span>
        </Row>
      ))}
    </dl>
  )
}

function AlertingPanel({ data }: { data: AdminSystem }) {
  return (
    <dl className="divide-y divide-line-soft">
      <Row label={<Code>OPS_ALERT_EMAIL</Code>}>
        <Code>{data.email.ops_alert_email}</Code>
      </Row>
      <Row label={<Code>EMAIL_FROM</Code>}>
        <Code>{data.email.from}</Code>
      </Row>
      <Row label="Email transport">
        <OnOff
          on={data.secrets.some((s) => s.name === 'RESEND_API_KEY' && s.present)}
          onLabel="Resend configured"
          offLabel="No RESEND_API_KEY: alerts are recorded, not emailed"
        />
      </Row>
    </dl>
  )
}

function ConfigPanel({ data }: { data: AdminSystem }) {
  return (
    <div className="flex flex-col gap-4">
      <dl className="divide-y divide-line-soft">
        <Row label="Build version">
          <Code>{data.version}</Code>
        </Row>
        <Row label={<Code>APP_ORIGIN</Code>}>
          <Code>{data.origins.app}</Code>
        </Row>
        <Row label={<Code>MARKETING_ORIGIN</Code>}>
          <Code>{data.origins.marketing}</Code>
        </Row>
        <Row label={<Code>DEMO_CLOCK</Code>}>
          {data.demo_clock ? (
            <Code>{data.demo_clock}</Code>
          ) : (
            <span className="text-sage">Empty: the server runs on real time</span>
          )}
        </Row>
        <Row label="Bindings">
          <span className="flex flex-wrap gap-1.5">
            {(
              [
                ['DB', data.bindings.db],
                ['KV', data.bindings.kv],
                ['FILES', data.bindings.files],
                ['ASSETS', data.bindings.assets],
              ] as const
            ).map(([name, on]) => (
              <OnOff key={name} on={on} onLabel={name} offLabel={`${name} missing`} />
            ))}
          </span>
        </Row>
      </dl>
      <div>
        <p className="text-sm font-medium text-sage">
          Secrets (presence only; values never leave the Worker)
        </p>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {data.secrets.map((secret) => (
            <li
              key={secret.name}
              className={cn(
                'flex items-center justify-between gap-2 rounded-sm border px-2.5 py-1.5 text-sm',
                secret.present
                  ? 'border-positive-line bg-positive-soft/50'
                  : 'border-line bg-cream',
              )}
            >
              <Code>{secret.name}</Code>
              <span className={secret.present ? 'text-positive' : 'text-sage'}>
                {secret.present ? 'Set' : 'Not set'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const TITLES: Record<LiveView, string> = {
  auth: 'sign-in settings on this deployment',
  feeds: 'data feeds on this deployment',
  cron: 'cron jobs registered in this build',
  alerting: 'alert email settings',
  config: 'this deployment’s configuration',
}

/** One live panel, fed by GET /admin/system. Fails soft: the docs still read without it. */
export function LivePanel({ view }: { view: LiveView }) {
  const system = useAdminSystem()
  return (
    <Frame title={TITLES[view]}>
      {system.isPending ? (
        <div className="flex flex-col gap-2 py-1" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : system.isError ? (
        <p className="text-sm text-sage">
          Live values are unavailable ({errorMessage(system.error)}). The text around this panel
          still describes the system.
        </p>
      ) : view === 'auth' ? (
        <AuthPanel data={system.data} />
      ) : view === 'feeds' ? (
        <FeedsPanel data={system.data} />
      ) : view === 'cron' ? (
        <CronPanel data={system.data} />
      ) : view === 'alerting' ? (
        <AlertingPanel data={system.data} />
      ) : (
        <ConfigPanel data={system.data} />
      )}
    </Frame>
  )
}
